import { VehicleAccidentState } from "@/api-schema-enums";
import type {
  VehicleUpdateBodyWithRegistrationDate,
  VehicleWithRegistrationDate,
} from "@/common/api/vehicle/vehicle.types";
import { dateToInputString, inputStringToDate, parseApiDateOnly } from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";
import { VEHICLE_KIND_LABELS } from "@/common/helpers/vehicle-type-labels";
import type { ComboboxOption } from "@/components/ui/combobox";
import type { VehicleFormMode, VehicleFormSchemaType } from "./vehicle-form-schema";

type ModelGeneration = NonNullable<VehicleWithRegistrationDate["modelGeneration"]>;
type VehicleBooleanFieldValue = VehicleFormSchemaType["vatDeductible"];
type VehicleBranchFormValues = Pick<VehicleFormSchemaType, "owningBranch" | "currentBranch">;

function toOptionalSelectValue<T extends string>(value: T | null | undefined): T | "" {
  return value ?? "";
}

function parseOptionalSelectValue<T extends string>(value: T | ""): T | null {
  return value || null;
}

function toVehicleAccidentStateSelectValue(
  value: VehicleAccidentState | null | undefined,
): VehicleFormSchemaType["accidentState"] {
  switch (value) {
    case VehicleAccidentState.AccidentFree:
    case VehicleAccidentState.ExistingDamage:
    case VehicleAccidentState.RepairedDamage:
    case VehicleAccidentState.UNKNOWN:
      return value;
    default:
      return "";
  }
}

function toVehicleBooleanSelectValue(value: boolean | null | undefined): VehicleBooleanFieldValue {
  if (value == null) {
    return "";
  }

  return value ? "true" : "false";
}

function parseVehicleBooleanSelectValue(value: VehicleBooleanFieldValue): boolean | null {
  if (!value) {
    return null;
  }

  return value === "true";
}

export function getVehicleBranchPayload(
  values: VehicleBranchFormValues,
  mode: "create",
): { owningBranch: string; currentBranch: string };
export function getVehicleBranchPayload(
  values: VehicleBranchFormValues,
  mode: "update",
): { owningBranch: string | null; currentBranch?: string };
export function getVehicleBranchPayload(values: VehicleBranchFormValues, mode: VehicleFormMode) {
  const owningBranch = values.owningBranch.trim();
  const currentBranch = values.currentBranch?.trim() || owningBranch;

  if (mode === "update") {
    return {
      owningBranch: owningBranch || null,
      ...(currentBranch ? { currentBranch } : {}),
    };
  }

  return { owningBranch, currentBranch };
}

export function toModelGenerationComboboxOption(modelGeneration: ModelGeneration, language: string): ComboboxOption {
  const vehicleKind =
    VEHICLE_KIND_LABELS[modelGeneration.vehicleKind]?.[language === "de" ? "de" : "en"] ?? modelGeneration.vehicleKind;
  const descriptionParts = [
    modelGeneration.modelFamily?.brand?.name,
    modelGeneration.modelFamily?.name,
    vehicleKind,
  ].filter(Boolean);
  const description = descriptionParts.join(" / ");
  const searchParts = [
    modelGeneration.name,
    modelGeneration.modelFamily?.name,
    modelGeneration.modelFamily?.brand?.name,
    vehicleKind,
    modelGeneration.vehicleKind,
  ].filter(Boolean);

  return {
    value: modelGeneration.uuid ?? "",
    label: modelGeneration.name || description,
    description: modelGeneration.name && description ? description : undefined,
    searchText: searchParts.join(" ").toLowerCase(),
  };
}

export function getVehicleFormValues(vehicle: VehicleWithRegistrationDate): VehicleFormSchemaType {
  return {
    modelGeneration: vehicle.modelGeneration?.uuid ?? "",
    modelDescription: vehicle.modelDescription ?? "",
    vin: vehicle.vin ?? "",
    licensePlate: vehicle.licensePlate ?? "",
    manufacturerColor: vehicle.manufacturerColor ?? "",
    color: toOptionalSelectValue(vehicle.color),
    registrationDate: dateToInputString(parseApiDateOnly(vehicle.registrationDate)),
    huDate: dateToInputString(parseApiDateOnly(vehicle.huDate)),
    serviceHistory: toOptionalSelectValue(vehicle.serviceHistory),
    vehicleUsage: toOptionalSelectValue(vehicle.vehicleUsage),
    rimType: toOptionalSelectValue(vehicle.rimType),
    exchangeEngineInstalled: !!vehicle.exchangeEngine,
    exchangeDate: dateToInputString(parseApiDateOnly(vehicle.exchangeEngine?.exchangeDate)),
    engineMileageAtExchange: vehicle.exchangeEngine?.engineMileageAtExchange?.toString() ?? "",
    vehicleMileageAtExchange: vehicle.exchangeEngine?.vehicleMileageAtExchange?.toString() ?? "",
    exchangeNotes: vehicle.exchangeEngine?.notes ?? "",
    drivetrain: toOptionalSelectValue(vehicle.drivetrain),
    vehicleKind: toOptionalSelectValue(vehicle.vehicleKind),
    accidentState: toVehicleAccidentStateSelectValue(vehicle.accidentState),
    accidentDescription: vehicle.accidentDescription ?? "",
    accidentValue: vehicle.accidentValue?.toString() ?? "",
    vatDeductible: toVehicleBooleanSelectValue(vehicle.vatDeductible),
    reimport: toVehicleBooleanSelectValue(vehicle.reimport),
    engineKilowatts: vehicle.engineKilowatts?.toString() ?? "",
    engineDisplacementCcm: vehicle.engineDisplacementCcm?.toString() ?? "",
    numberDoors: vehicle.numberDoors?.toString() ?? "",
    mileage: vehicle.mileage?.toString() ?? "",
    owningBranch: vehicle.owningBranch?.uuid ?? "",
    currentBranch: vehicle.currentBranch?.uuid ?? vehicle.owningBranch?.uuid ?? "",
  };
}

export function getVehicleFormPayload(values: VehicleFormSchemaType) {
  return {
    modelGeneration: values.modelGeneration,
    modelDescription: values.modelDescription.trim() || null,
    vin: values.vin.trim().toUpperCase(),
    licensePlate: values.licensePlate.trim() || null,
    manufacturerColor: values.manufacturerColor.trim() || null,
    color: parseOptionalSelectValue(values.color),
    registrationDate: values.registrationDate
      ? formatDateToApi(inputStringToDate(values.registrationDate)!)
      : undefined,
    huDate: values.huDate ? formatDateToApi(inputStringToDate(values.huDate)!) : null,
    serviceHistory: parseOptionalSelectValue(values.serviceHistory),
    vehicleUsage: parseOptionalSelectValue(values.vehicleUsage),
    rimType: parseOptionalSelectValue(values.rimType),
    drivetrain: parseOptionalSelectValue(values.drivetrain),
    vehicleKind: parseOptionalSelectValue(values.vehicleKind),
    accidentState: parseOptionalSelectValue(values.accidentState),
    accidentDescription: values.accidentDescription.trim() || null,
    accidentValue: values.accidentValue.trim() ? Number(values.accidentValue) : null,
    vatDeductible: parseVehicleBooleanSelectValue(values.vatDeductible),
    reimport: parseVehicleBooleanSelectValue(values.reimport),
    engineKilowatts: values.engineKilowatts.trim() ? Number.parseInt(values.engineKilowatts, 10) : null,
    engineDisplacementCcm: values.engineDisplacementCcm.trim()
      ? Number.parseInt(values.engineDisplacementCcm, 10)
      : null,
    numberDoors: values.numberDoors.trim() ? Number.parseInt(values.numberDoors, 10) : null,
    mileage: values.mileage.trim() ? Number.parseInt(values.mileage, 10) : null,
  } satisfies VehicleUpdateBodyWithRegistrationDate;
}
